import type { Label } from "../NodeTemplate/types";
import type {
  AddonItem,
  AddonInstancePhase,
  AllVpcInfoSubnet,
  MachineTypeEnum,
  NodeNameMode,
  AddonInstance,
} from "./api/types";
import type { Model as EpcCreateStepPackageModel } from "@kce2/common/epc-create/components/StepPackage/types";
import type { Model as EpcImageModel } from "@kce2/common/epc-create/components/StepImage/types";
import type { EssdPL } from "../../common/kec";
import type { Option } from "@kce2/common/core/types";
import type { ImageType } from "./utils/image";

export type LabelOperation = "Equal" | "NotEqual" | "In" | "NotIn" | "Exists" | "DoesNotExist";

export type LabelFilter = {
  id: number;
  label: string;
  op: LabelOperation;
  value: string;
};

export type ClusterPriceItem = {
  item: string;
  // 后期可已考虑需要优化成单个计费项是否在计算
  // isCalculating: boolean;
  combinator?: string;
  prices: {
    prefix?: string;
    price: number;
    unit?: string;
    billTypeName: string;
    billTypeId?: number;
  }[];
};

export type CNIFormModel = {
  enabled: boolean;
  networkMode: VpcCNIDaemonMode;
  subnets: AllVpcInfoSubnet[];
  securityGroupId: string | undefined;
  fixPodIP: boolean;
};

export type DataDiskModel = {
  IsLocal?: boolean;
  DiskType: string;
  DiskSize: number;
  FileSystem?: string;
  AutoFormatAndMount?: boolean;
  MountTarget?: string;
  DeleteWithInstance?: boolean;
  ProductType?: number;
  PerfLevel: EssdPL | "";
};

export type BaseServerModel = {
  // nodeSource: "new" | "exists";
  nodeType: MachineTypeEnum.MachineKec;

  billType: number;
  buyTime: number;
  subnet: string;
  imageId: string;
  imageType: ImageType;
  imageSystem: string;
  isGpu: boolean;
  isCommon: boolean;
  isArm: boolean;
  isEnhance: boolean;
  generation: string;
  // imageFull: ImageOption;

  systemDiskType: string;
  systemDiskSize: number;
  systemDiskEssdPerfLevel: EssdPL | "";
  systemDiskSizeAdjustable: boolean;
  ebsDataDisks: DataDiskModel[];
  localDisk?: DataDiskModel;
  nodeNameType: NodeNameMode;
  instanceLabels: Label[];
};

export type MachineInfo = {
  instanceName: string;
  instanceId: string;
};
export type ExistedServerModel = BaseServerModel & {
  installMethod: "AUTO";
  nodeSource: "exists";
  machines: MachineInfo[];
};
export type BaseCreateServerModel = {
  machineProductTypeId: number;
  machineProductTypeName: string;
  machineSpecCode: string;
  machineSpecName: string;
  machineCpu: number;
  machineMem: number;
  machineGpu: string;
  instanceName: string;
};
export type NewServerModel = BaseServerModel &
  BaseCreateServerModel & {
    nodeSource: "new";
    templateId?: string;
  };

export type ServerModel =
  | ExistedServerModel
  | NewServerModel
  | ExistedEpcSelectionModel
  | NewEpcServerModel;

export type EpcInstallMethod = "MANUAL" | "AUTO";
export type ExistedEpcSelectionModel = {
  billType: -1;
  subnet: "";
  nodeType: MachineTypeEnum.MachineEpc;
  nodeSource: "exists";
  series: "CAL" | "GPU";
  archCode: string;
  machines: (MachineInfo & { installMethod?: EpcInstallMethod })[];
  imageId: string;
  imageType?: ImageType;
  nodeNameType?: NodeNameMode;
  instanceLabels: Label[];
  reinstall: boolean;
  installMethod: EpcInstallMethod;
  // 重装配置
  reinstallType?: "custom" | "quick";
  computerName?: string;
  // RAID
  raid?: string;
  raidId?: string;
  // Bond
  networkInterfaceMode?: string;
  bondAttribute?: string;
  // 高级选项
  systemFileType?: string;
  dataDiskMount?: string;
  dataFileType?: string;
  dataDiskCatalogue?: string;
  dataDiskCatalogueSuffix?: string;
  advanceNvmeSetting?: string;
  nvmeDataFileType?: string;
  nvmeDataDiskCatalogue?: string;
  nvmeDataDiskCatalogueSuffix?: string;
  // GPU 驱动
  gpuImageDriverId?: string;
};

export type NewEpcServerModel = Omit<EpcCreateStepPackageModel, "billType"> & {
  templateId?: string;
  subnet: string;
  vpcId: string;
  billType: number;
  billTypeFull: EpcCreateStepPackageModel["billType"];
  buyTime?: number;
  nodeType: MachineTypeEnum.MachineEpc;
  nodeSource: "new";
  imageModel: EpcImageModel;
  imageId: string;
  instanceName: string;
  nodeNameType: NodeNameMode;
  machineSpecCode: string;
  // machineProductTypeName: string;
  machineCpu: string;
  machineMem: string;
  machineGpu: string;
  networkInterface: string;
  bondName: string;
  raidType?: string;
  raidId?: string;
  systemDiskFileSystem: string;
  // systemDiskSize: string; // ?
  // dataDiskType: string;
  /**
   * @type {'support' | 'unsupport'}
   */
  dataDiskMount: string;
  dataDiskFileSystem: string;
  dataDiskMountTarget: string;
  dataDiskSuffix: string;
  advanceNvmeSetting?: boolean | string;
  nvmeDataFileType?: string;
  nvmeDataDiskCatalogue?: string;
  nvmeDataDiskCatalogueSuffix?: string;
  archCode: string;
};

export type NewEpcServerSetModel = Omit<
  NewEpcServerModel,
  "machineCpu" | "machineMem" | "machineGpu"
>;

// export type VpcCNIConfig = {
//   DAEMON_MODE: string;
//   POD_NETWORKS: string;
//   REGION: string;
//   SECURITY_GROUP: string;
//   SERVICE_CIDR: string;
// };

export type VpcCNIDaemonMode = "eniOnly" | "eniMultiIP" | (string & {});
export type VpcCNIConfig = {
  eni_conf: {
    security_group: string;
    pod_networks: Record<string, string[]>;
    daemon_mode: VpcCNIDaemonMode;
    support_fixed_ip: boolean;
  };
};

export type VpcCNIParsedConfig = {
  SecurityGroup: string;
  DaemonMode: VpcCNIDaemonMode;
  PodNetworks: Record<string, string[]>;
  SupportFixedIP: boolean;
};

export type NodeSource = ServerModel["nodeSource"];

export type ClusterDetailMenuItemKeys =
  | "clusterInfo"
  | "nodeManager-node"
  | "nodeManager-vnode"
  | "nodeManager-nodePool"
  | "network-gateway"
  | "addonManager"
  | "aiAddonManager"
  | "monitor-cplog"
  | "logManager"
  | "eventCenter"
  | "backup"
  | "controlPlaneArgs";

export enum SubnetStrategy {
  Order = "Order",
  Random = "Random",
}

export function subnetStrategyToText(v: any) {
  switch (v) {
    case SubnetStrategy.Order:
      return "顺序选择";
    case SubnetStrategy.Random:
      return "随机选择";
    default:
      return "";
  }
}

export type AddonInfo = {
  Name: string;
  AddonId: string;
  InstanceId: string;
  ClusterId: string;
  Category: string;
  Type: string;
  TypeText: string;
  CurrentVersion: string;
  AddonVersion: string;
  Description: string;
  Status: AddonInstancePhase | "NotInstalled";
  Reason: string;
  Message: string;
  Args: Record<string, string>;
  IsMultiple: boolean;
  AddonItem: AddonItem;
  Instance: AddonInstance | null;
  InstanceNumber: number;
  DependIamRole: string;
};

export type AzOptionForNew = Option<{
  supportsKec: boolean;
  supportsEpc: boolean;
  // maybe other fields
}>;

export type KubeConfigLoadingStatus =
  | "fetch-error"
  | "empty"
  | "no-permission"
  | "loading"
  | "done"
  | "no-options";

// 此处值需要调整
export type KubeConfigType =
  | "INNERSLB"
  | "OUTERSLB"
  | "PRIVATELINK"
  | "PRIVATELINK_DOMAIN"
  | "MANAGED";

export type ControlPlaneComponentNames = "APISERVER" | "CONTROLLERMANAGER" | "SCHEDULER";

export type ClusterComponentRunningStatusValues =
  | "Running" // 正常
  | "TotallyError" // 完全异常
  | "PartiallyError"; //部分异常

export type ClusterComponentOperationStatusValues =
  | "Updating" // 更新中
  | "RollBacking" // 回退中
  | "UpdateFailed" // 更新失败
  | "RollBackFailed" // 回退失败
  | "UpdateSuccess" // 更新成功
  | "RollBackSuccess"; // 回滚成功

export type ControlPlaneArgsValues = string | undefined;
export type ControlPlaneArgsAllValues = Record<string, ControlPlaneArgsValues>;
export type ArgChange = {
  name: string;
  oldValue: string;
  newValue: string;
  isOtherArgsKey?: boolean;
};

export type Kce1MigrationProgressStatus =
  | "MigrateStatusMigrating"
  | "MigrateStatusSucceed"
  | "MigrateStatusTimeout"
  | "MigrateStatusFailed";

export namespace Kce1MigrationProgressStatus {
  export const Migrating = "MigrateStatusMigrating";
  export const Succeed = "MigrateStatusSucceed";
  export const Timeout = "MigrateStatusTimeout";
  export const Failed = "MigrateStatusFailed";
}

export type Kce1MigrationProgressStepStatus = "success" | "failed" | "running" | "pending";
export namespace Kce1MigrationProgressStepStatus {
  export const Success = "success";
  export const Failed = "failed";
  export const Running = "running";
  export const Pending = "pending";
}

export type Kce1MigrationProgressStep =
  | "environmentPreparation"
  | "certificateMigration"
  | "nodeResourceMigration"
  | "networkMigration"
  | "controlPlaneMigration"
  | "postMigrationCleanup";

export namespace Kce1MigrationProgressStep {
  export const EnvironmentPreparation = "environmentPreparation";
  export const CertificateMigration = "certificateMigration";
  export const NodeResourceMigration = "nodeResourceMigration";
  export const NetworkMigration = "networkMigration";
  export const ControlPlaneMigration = "controlPlaneMigration";
  export const PostMigrationCleanup = "postMigrationCleanup";
}
